import { useCallback, useContext, useEffect, useState, useMemo, useRef } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { Image, Flex } from "uikit-react";
import { useCookies } from "react-cookie";
import classnames from "classnames";
import moment from "moment";
// Import common components
import Pagination from "../../common/Pagination";
import RecordsPerPage from "../../common/RecordsPerPage";
import AddTextLine from "../../common/AddTextLine";

// Import work-order components
import WorkOrderAdd from "../work_order_add";
import WorkOrderDelete from "../work_order_delete";
import FilterPanel from "./FilterPanel";
import WorkOrderRow from "./WorkOrderRow";

import { AppContexts } from "../../../providers";
import TableLoading from "../../common/Loading/table_loading";
import Tags from "../../common/Tags";
import { Link } from "../../../ui";
import { useDeepCompareEffect } from "../../../hook";
import { CSVLink } from "react-csv";
import { getPriorityLabel } from "../priorityOptions";
import { WORK_ORDER_CATEGORY_OPTIONS } from "../categoryOptions";
import { TASK_TYPE_LABELS } from "../taskTypeOptions";
import {
  isRecurringWorkOrder,
  promptRecurringEditScope,
} from "../recurringScopePrompt";
import { openWorkOrderAddPanel } from "../workOrderAddPanel";

export const DEFAULT_CONFIG = {
  pageTitle: "Work Orders",
  documentTitle: "Work Orders - RueBaRue",
  itemLabel: "Work Order",
  itemLabelPlural: "Work Orders",
  showCreateButton: true,
  showFilters: true,
  showCSVExport: true,
  showTags: true,
  showDelete: true,
  showEdit: true,          // true | false | "owner_only"
  urlSync: true,
  fetchTags: true,
  extraCriteria: {},       // merged into every fetch (e.g. { my_tasks: "true" })
  addTextLineFeature: "Work Orders",
  emptyStateLabel: "work orders",
  detailBasePath: "/work-orders",
  showTaskType: false,
  showRecurrence: false,
  taskTypeLabel: "Task Type*",
};

const baseDefaultCriteria = {
  search_term: "",
  filters: "new",
  sort: "updated_at",
  category: "",
  priority: "",
  assignee: "",
  property: "",
  tag: "",
  date_from: "",
  date_to: "", 
  page: 0,
  per: "20",
};

const buildDefaultCriteria = (cfg) => ({
  ...baseDefaultCriteria,
  ...(cfg.showTaskType ? { task_type: "" } : {}),
});

const sortOptions = [
  { key: "updated_at", title: "Last Updated" },
  { key: "created_at", title: "Created At" },
  { key: "summary", title: "Summary" },
  { key: "id", title: "Order Number" },
  { key: "assignee", title: "Assignee" },
];

const filterOptions = [
  { key: "new", title: "New" },
  { key: "started", title: "Started" },
  { key: "completed", title: "Completed" },
  { key: "closed", title: "Closed" },
];

const parseQueryString = (queryString, defaultCriteria) => {
  return {
    ...defaultCriteria,
    ...Object.fromEntries(new URLSearchParams(queryString)),
  };
};

const WorkOrderList = ({ config: configProp } = {}) => {
  const cfg = { ...DEFAULT_CONFIG, ...configProp };
  const defaultCriteria = buildDefaultCriteria(cfg);
  const params = useParams();

  const settingsContext = useContext(AppContexts.SettingsContext);
  const rentalContext = useContext(AppContexts.RentalsContext);
  const workOrderContext = useContext(AppContexts.WorkOrderContext);

  const { user } = useContext(AppContexts.AuthContext);

  const { workOrders, total, loading, attributes } = workOrderContext;
  const { rentals } = rentalContext;

  const [cookies] = useCookies(["records-per-page"]);
  const { "records-per-page": recordsPerPage = 20 } = cookies;
  const filterDropdownRef = useRef(null);

  const location = useLocation();
  const history = useHistory();
  const [criteria, setCriteria] = useState(() => {
    return location.search
      ? parseQueryString(location.search, defaultCriteria)
      : defaultCriteria;
  });

  const [filterDraft, setFilterDraft] = useState({
    priority: "",
    task_type: "",
    category: "",
    tag: "",
    assignee: "",
    property: "",
    date_from: "",
    date_to: "",
  });

  useEffect(() => {
    setFilterDraft((prev) => ({
      ...prev,
      priority: criteria.priority || "",
      task_type: criteria.task_type || "",
      category: criteria.category || "",
      tag: criteria.tag || "",
      assignee: criteria.assignee || "",
      property: criteria.property || "",
      date_from: criteria.date_from || "",
      date_to: criteria.date_to || "",
    }));
  }, [
    criteria.priority,
    criteria.task_type,
    criteria.category,
    criteria.tag,
    criteria.assignee,
    criteria.property,
    criteria.date_from,
    criteria.date_to,
  ]);  

  const updateQueryParams = (newCriteria) => {
    if (!cfg.urlSync) return;
    const sanitizedCriteria = cfg.showTaskType
      ? newCriteria
      : Object.fromEntries(
          Object.entries(newCriteria).filter(([key]) => key !== "task_type"),
        );
    const searchParams = new URLSearchParams(sanitizedCriteria).toString();
    const pathname = history.location.pathname;
    if (pathname !== '/work-orders') {
      return;
    } else {
      history.replace(`${history.location.pathname}?${searchParams}`);
    }
  };

  const [rentalsBootstrapped, setRentalsBootstrapped] = useState(false);

  useEffect(() => {
    if (!rentalsBootstrapped && (rentalContext.rentals?.length || params.slug)) {
      // once we have *any* rentals (or slug mode where rentals come via attributes),
      // stop treating rental loading as page-preloading forever
      setRentalsBootstrapped(true);
    }
  }, [rentalsBootstrapped, rentalContext.rentals?.length, params.slug]);
  
  const preloading =
    (!rentalsBootstrapped && rentalContext.loading) ||
    settingsContext.loading ||
    loading;

  useEffect(() => {
    document.title = cfg.documentTitle;
    if (user) {
      rentalContext.get();
      if (cfg.fetchTags) settingsContext.get(["tags"]);
      workOrderContext.getAttributes(null, ["assigners"]);
    }

    if (params.slug) {
      workOrderContext.getAttributes(params.slug, [
        ...(cfg.fetchTags ? ["tags"] : []),
        "rentals",
        "assigners",
      ]);
    }
  }, []);  

  useEffect(() => {
    setCriteria((prev) => ({ ...prev, per: recordsPerPage }));
  }, [recordsPerPage]);

  useEffect(() => {
    if (workOrderContext.attributes && params.slug) {
      let { rentals, tags } = workOrderContext.attributes;

      if (rentals?.length) rentalContext.setRentals(rentals);
      if (tags?.length)
        settingsContext.setSettings({
          ...settingsContext.settings,
          tags: tags,
        });
    }
  }, [params.slug, workOrderContext.attributes]);

  const orders = useMemo(() => workOrders || [], [workOrders]);
  const propertyValues = useMemo(
    () =>
      (rentalContext.rentals || [])
        .filter((r) => r.pms_id)
        .sort((a, b) => a.pms_id.localeCompare(b.pms_id)) || [],
    [rentalContext.rentals],
  );
  const tagValues = useMemo(
    () =>
      (settingsContext.settings.tags || []).sort((a, b) =>
        a.name.localeCompare(b.name),
      ) || [],
    [settingsContext.settings],
  );
  const assignees = useMemo(() => {
    let { recipients, contacts, assignees } =
      workOrderContext.attributes.assigners;

    return [
      ...(recipients || [])
        .filter((recipient) => {
          return !!recipient.user_id && !recipient.deleted;
        })
        .map((recipient) => {
          return {
            label: `${recipient.first_name} ${recipient.last_name}`,
            value: `${recipient.recipient_id}`,
          };
        }),
      ...(contacts || [])
        .filter((contact) => {
          return !!assignees.find(
            (a) =>
              a.assignee_type === "contact" && +a.assignee_id === +contact.id,
          );
        })
        .map((contact) => {
          const cat = contact.category ? contact.category.charAt(0).toUpperCase() + contact.category.slice(1) : '';
          const baseName = `${contact.first_name} ${contact.last_name}`;
          return {
            label: cat ? `${baseName} (${cat})` : baseName,
            value: `${contact.id}`,
          };
        }),
    ];
  }, [workOrderContext.attributes]);

  // Resolve the current user's recipient_id from the assigners list
  const userRecipientId = useMemo(() => {
    const { recipients } = workOrderContext.attributes.assigners || {};
    const match = (recipients || []).find((r) => String(r.user_id) === String(user?.user_id));
    return match ? String(match.recipient_id) : null;
  }, [workOrderContext.attributes, user]);

  useDeepCompareEffect(() => {
    let timeoutId;

    updateQueryParams(criteria);
    const criteriaForFetch = cfg.showTaskType
      ? criteria
      : Object.fromEntries(
          Object.entries(criteria).filter(([key]) => key !== "task_type"),
        );
    const fetchCriteria = { ...criteriaForFetch, ...cfg.extraCriteria };
    timeoutId = setTimeout(
      () => workOrderContext.getWorkOrders(params.slug, fetchCriteria),
      500,
    );
    return () => {
      clearTimeout(timeoutId);
    };
  }, [criteria]);

  const property = useCallback(
    (id) => rentals.find((rental) => +rental.rental_id === +id),
    [orders, rentals],
  );
  const handlePageChange = useCallback((pageIdx) =>
    handleCriteriaChange("page", pageIdx),
  );

  const handleAddWorkOrder = useCallback(() => {
    workOrderContext.invalidateWorkOrderFetch?.();
    workOrderContext.setRecurringEditScope?.(null);
    workOrderContext.setIsNew(true);
    workOrderContext.initWorkOrder();
  }, [workOrderContext]);

  const handleEditWorkOrder = useCallback(
    async (workOrder) => {
      const id = workOrder?.id ?? workOrder;
      if (!id) return;

      workOrderContext.invalidateWorkOrderFetch?.();
      workOrderContext.setIsNew(false);
      workOrderContext.setRecurringEditScope?.(null);

      const wo = typeof workOrder === "object" ? workOrder : null;
      if (wo && isRecurringWorkOrder(wo)) {
        const scope = await promptRecurringEditScope({
          itemLabel: cfg.itemLabel,
          summary: wo.summary,
          intent: "open",
        });
        if (!scope) return;
        workOrderContext.setRecurringEditScope?.(scope);
      }

      await workOrderContext.getWorkOrder(id);
      openWorkOrderAddPanel();
    },
    [cfg.itemLabel, workOrderContext]
  );

  const handleClickDelete = (workOrder) =>
    workOrderContext.setWorkOrder(workOrder);

  const getAssignees = (workOrder) =>
    workOrder.work_order_assignees
      ?.filter((assignee) => assignee.assignment_role !== 'owner') // Only regular assignees, not owners
      ?.map((assignee) => assignee.assignee_name)
      .filter((name) => name)
      .join(", ");

  const getOwner = (workOrder) => {
    const owner = workOrder.work_order_assignees
      ?.find((assignee) => assignee.assignment_role === 'owner');
    
    return owner?.assignee_name || null;
  };

  const diffDate = (time) => {
    let diff = moment.duration(moment().diff(moment.utc(time).local()));

    let year = diff.years();
    let month = diff.months();
    let day = diff.days();
    let hour = diff.hours();

    if (year) {
      return `${year} year(s) ago`;
    } else if (month) {
      return `${month} month(s) ago`;
    } else if (day) {
      return `${day} day(s) ago`;
    } else if (hour) {
      return `${hour} hour(s) ago`;
    }
  };

  const handleSelectedTags = useCallback((tags, order) => {
    let updated = { ...order, tags };
    workOrderContext.updateWorkOrder(updated.id, updated);
  }, []);

  const handleCriteriaChange = useCallback((key, value) => {
    setCriteria((criteria) => ({ ...criteria, [key]: value }));
  });

  const data = useMemo(() => {
    return orders.map((workOrder) => {
      const rental = property(workOrder?.rental_id);

      return [
        workOrder.id,
        workOrder.summary,
        workOrder.description,
        workOrder.status, // or state
        workOrder.priority,
        workOrder.category,
        getAssignees(workOrder),
        workOrder.reporter?.first_name + " " + workOrder.reporter?.last_name,
        rental?.pms_id,
        rental?.name,
        rental?.address,
        workOrder?.tags?.map(t => t.name)?.join(', '),
        moment.utc(workOrder.created_at).local().format("MM/DD/YYYY hh:mm A"),
        moment.utc(workOrder.updated_at).local().format("MM/DD/YYYY hh:mm A"),
      ];
    });
  }, [orders, rentals]);

  const headers = useMemo(() => {
    if (orders.length === 0) return [];
    return [
      "#",
      "Summary",
      "Description",
      "Status",
      "Priority",
      "Category",
      "Assigned To",
      "Reporter",
      "Property ID",
      "Property Name",
      "Address",
      "Tags",
      "Created At",
      "Updated At",
    ];
  }, [orders]);

  return (
    <>
      <section id="work-order-template">
        <AddTextLine feature={cfg.addTextLineFeature} />
        <div id="hms-page-title">
          <h1 className="uk-heading-small">{cfg.pageTitle}</h1>
        </div>
        <div id="hms-main-body">
          {(cfg.showCreateButton || cfg.showCSVExport) && (
          <div className="uk-flex uk-flex-between uk-flex-top">
            {cfg.showCreateButton ? (
              <div className="hms-guest-btn">
                <button
                  className="uk-button hms-btn hms-red-btn"
                  uk-toggle="target: #work-order-add"
                  onClick={handleAddWorkOrder}
                >
                  <span uk-icon="icon: plus; ratio: 0.7"></span> {cfg.itemLabel}
                </button>
              </div>
            ) : <div />}
            {cfg.showCSVExport && (
              <CSVLink
                data={data}
                filename={`${moment().unix()}_work_order.csv`}
                headers={headers}
              >
                <button className="download">
                  <img src="/images/download.svg" uk-tooltip="Download CSV" />
                </button>
              </CSVLink>
            )}
          </div>
          )}
          <div className="uk-card uk-card-small uk-card-default hms-form-card card-without-filters">
            <div className="uk-overflow-auto">
              <form action="" className="uk-form">
                <Flex
                  alignment="between top"
                  className="workorder-form-top-filters"
                >
                  {/* LEFT: Search + Filters */}
                  <div className="hms-form hms-form-inline">
                    {/* SEARCH */}
                    <div className="uk-inline hms-search-inline">
                      <div className="uk-search uk-search-default">
                        {criteria.search_term && criteria.search_term !== "" ? (
                          <a
                            className="uk-form-icon-flip uk-form-icon"
                            uk-icon="icon: close"
                            onClick={() => handleCriteriaChange("search_term", "")}
                          ></a>
                        ) : (
                          <span
                            className="uk-search-icon-flip"
                            uk-search-icon="true"
                          />
                        )}
                        <input
                          className="uk-search-input"
                          type="search"
                          placeholder="Order # or Summary"
                          value={criteria.search_term}
                          onChange={(e) =>
                            handleCriteriaChange("search_term", e.target.value)
                          }
                        />
                      </div>
                    </div>

                    {/* FILTERS DROPDOWN BUTTON */}
                    {cfg.showFilters && (
                      <FilterPanel
                        ref={filterDropdownRef}
                        filterDraft={filterDraft}
                        setFilterDraft={setFilterDraft}
                        setCriteria={setCriteria}
                        preloading={preloading}
                        tagValues={tagValues}
                        assignees={assignees}
                        propertyValues={propertyValues}
                        showTaskType={cfg.showTaskType}
                      />
                    )}
                  </div>

                  {/* RIGHT: Sort */}
                  <div>
                    <div className="hms-sortby-btn">
                      <button className="uk-button" type="button">
                        SORT BY<span uk-icon="icon: triangle-down"></span>
                      </button>
                      <div uk-dropdown="mode: click; offset: 0; animation: uk-animation-slide-top-small; duration: 400">
                        <ul className="uk-nav uk-dropdown-nav">
                          {sortOptions.map((option, idx) => (
                            <li
                              className={classnames({
                                "uk-active": criteria.sort === option.key,
                              })}
                              key={`sort_option_${idx}`}
                            >
                              <Link
                                href="#"
                                onClick={() =>
                                  handleCriteriaChange("sort", option.key)
                                }
                              >
                                {option.title}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </Flex>
                {cfg.showFilters && (
                  criteria.priority ||
                  (cfg.showTaskType && criteria.task_type) ||
                  criteria.category ||
                  criteria.tag ||
                  criteria.assignee ||
                  criteria.property ||
                  criteria.date_from ||
                  criteria.date_to
                ) && (
                  <div className="hms-active-filters uk-margin-small-top uk-margin-small-bottom">
                    <span className="hms-active-filters-label">Filters:</span>

                    {criteria.priority && (
                      <span className="hms-filter-chip">
                        Priority: {getPriorityLabel(criteria.priority)}
                        <button
                          type="button"
                          className="hms-filter-chip-close"
                          onClick={() => handleCriteriaChange("priority", "")}
                        >
                          ×
                        </button>
                      </span>
                    )}

                    {cfg.showTaskType && criteria.task_type && (
                      <span className="hms-filter-chip">
                        Task Type: {TASK_TYPE_LABELS[criteria.task_type] || criteria.task_type}
                        <button
                          type="button"
                          className="hms-filter-chip-close"
                          onClick={() => handleCriteriaChange("task_type", "")}
                        >
                          ×
                        </button>
                      </span>
                    )}

                    {criteria.category && (
                      <span className="hms-filter-chip">
                        Category: {
                          (WORK_ORDER_CATEGORY_OPTIONS.find(
                            (o) => o.value === criteria.category,
                          ) || {}).label || criteria.category
                        }
                        <button
                          type="button"
                          className="hms-filter-chip-close"
                          onClick={() => handleCriteriaChange("category", "")}
                        >
                          ×
                        </button>
                      </span>
                    )}

                    {criteria.tag && (
                      <span className="hms-filter-chip">
                        Tag: {(tagValues.find((t) => `${t.id}` === `${criteria.tag}`) || {}).name || criteria.tag}
                        <button
                          type="button"
                          className="hms-filter-chip-close"
                          onClick={() => handleCriteriaChange("tag", "")}
                        >
                          ×
                        </button>
                      </span>
                    )}

                    {criteria.assignee && (
                      <span className="hms-filter-chip">
                        Assignee: {(assignees.find((a) => a.value === criteria.assignee) || {}).label || criteria.assignee}
                        <button
                          type="button"
                          className="hms-filter-chip-close"
                          onClick={() => handleCriteriaChange("assignee", "")}
                        >
                          ×
                        </button>
                      </span>
                    )}

                    {criteria.property && (
                      <span className="hms-filter-chip">
                        Property: {
                          (propertyValues.find(
                            (p) => `${p.rental_id}` === `${criteria.property}`,
                          ) || {}).pms_id || criteria.property
                        }
                        <button
                          type="button"
                          className="hms-filter-chip-close"
                          onClick={() => handleCriteriaChange("property", "")}
                        >
                          ×
                        </button>
                      </span>
                    )}

                    {(criteria.date_from || criteria.date_to) && (
                      <span className="hms-filter-chip">
                        Date: {criteria.date_from || "…"} – {criteria.date_to || "…"}
                        <button
                          type="button"
                          className="hms-filter-chip-close"
                          onClick={() =>
                            setCriteria((prev) => ({
                              ...prev,
                              date_from: "",
                              date_to: "",
                              page: 0,
                            }))
                          }
                        >
                          ×
                        </button>
                      </span>
                    )}

                    <button
                      type="button"
                      className="hms-filter-chip-clear-all"
                      onClick={() =>
                        setCriteria((prev) => ({
                          ...prev,
                          priority: "",
                          ...(cfg.showTaskType ? { task_type: "" } : {}),
                          category: "",
                          tag: "",
                          assignee: "",
                          property: "",
                          date_from: "",
                          date_to: "",
                          page: 0,
                        }))
                      }
                    >
                      Clear all
                    </button>
                  </div>
                )}                
              </form>
              <div className="uk-flex uk-flex-between uk-margin-small-bottom">
                <div>
                  <ul data-uk-tab>
                    {filterOptions.map((option, idx) => (
                      <li
                        className={classnames({
                          "uk-active": criteria.filters === option.key,
                        })}
                        key={`filter_option_${idx}`}
                      >
                        <Link
                          href="#"
                          onClick={() =>
                            handleCriteriaChange("filters", option.key)
                          }
                        >
                          {option.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <table className="uk-table uk-table-hover uk-table-divider hms-table">
                <thead>
                <tr>
                  <th>#</th>
                  <th className="uk-table-expand">
                    Summary / Property
                  </th>
                  <th>{cfg.showTaskType ? "Type / Status / Priority" : "Status / Priority / Category"}</th>
                  <th>Assigned To</th>
                  <th>Checklist</th>
                  <th>Cost</th>
                  {cfg.showTags && <th>Tags</th>}
                  <th>Updated At</th>
                  <th>Actions</th>
                </tr>
              </thead>
                <tbody>
                  {loading ? (
                    TableLoading(11)
                  ) : orders.length > 0 ? (
                    orders.map((workOrder, idx) => {
                      const rental = property(workOrder.rental_id);
                      const statusLabel = workOrder.status || workOrder.state || criteria.filters;
                      
                      return (
                        <WorkOrderRow
                          key={`workorder_${idx}`}
                          workOrder={workOrder}
                          idx={idx}
                          rental={rental}
                          statusLabel={statusLabel}
                          getAssignees={getAssignees}
                          getOwner={getOwner}
                          diffDate={diffDate}
                          handleSelectedTags={handleSelectedTags}
                          handleEditWorkOrder={handleEditWorkOrder}
                          handleClickDelete={handleClickDelete}
                          slug={params.slug}
                          showTags={cfg.showTags}
                          showDelete={cfg.showDelete}
                          showEdit={cfg.showEdit}
                          user={user}
                          userRecipientId={userRecipientId}
                          showTaskType={cfg.showTaskType}
                          itemLabel={cfg.itemLabel}
                          detailBasePath={cfg.detailBasePath}
                        />
                      );
                    })
                  ) : (
                    <tr>
                      <td
                        colSpan={10}
                        style={{ padding: "30px 25px", textTransform: "none" }}
                      >
                        No {cfg.emptyStateLabel} matching your current search or filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <Flex alignment="between top" className="guest-form-top-filters">
            <Pagination
              count={total}
              index={Number(criteria.page)}
              onIndexChanged={handlePageChange}
            />
            {total > 0 && <RecordsPerPage />}
          </Flex>
        </div>
      </section>
      {(cfg.showCreateButton || cfg.showEdit) && (
        <WorkOrderAdd
          itemLabel={cfg.itemLabel}
          taskTypeLabel={cfg.taskTypeLabel}
          showTaskType={cfg.showTaskType}
          showRecurrence={cfg.showRecurrence}
        />
      )}
      {cfg.showDelete && (
        <WorkOrderDelete itemLabel={cfg.itemLabel} closeEditOnDelete />
      )}
    </>
  );
};

export default WorkOrderList;
